import { roundTo } from "./mathUtils";

// ============================================
// FUNCIÓN 1: Calcular paneles necesarios desde kWh
// ============================================
export const calcularPanelesNecesarios = (consumoKwh, config) => {
  const { KWH_MES_PANEL } = config;

  if (consumoKwh < KWH_MES_PANEL) {
    return {
      error: true,
      message: `El consumo es muy pequeño y no es recomendable instalar paneles solares, para la instalación de paneles el consumo debe ser mayor a ${KWH_MES_PANEL} kWh`,
      panelesNecesarios: 0,
    };
  }

  const panelesNecesarios = Math.ceil(consumoKwh / KWH_MES_PANEL);

  return {
    error: false,
    message: "",
    panelesNecesarios: panelesNecesarios,
  };
};

// ============================================
// FUNCIÓN 2: Calcular todo desde paneles (REUTILIZABLE)
// ============================================
export const calcularSistemaDesdePanel = (
  numeroPaneles,
  regionId = 1,
  consumoOriginal = 0,
  config
) => {
  const {
    paneles_solares,
    guatemalaRegions,
    KWH_MES_PANEL,
    COSTO_ENERGIA_PROM_SINPANEL,
    MAX_PANELES_POR_SISTEMA,
    COSTO_ENERGIA_PROM_CONPANEL,
    FACT_CO2_PROMEDIO_ANUAL,
    PORCENTAJE_PAGO_FIJO,
    DESCUENTO_CONTADO,
  } = config;

  // Calcular la inversión total considerando múltiples sistemas si es necesario
  const calcularInversion = (totalPaneles) => {
    let inversionGeneral = 0;
    let panelesRestantes = totalPaneles;
    const desgloseSistemas = [];

    while (panelesRestantes > 0) {
      const panelesEnEsteSistema = Math.min(
        panelesRestantes,
        MAX_PANELES_POR_SISTEMA
      );

      // Buscar en el JSON la inversión para esta cantidad de paneles
      const sistemaData = paneles_solares?.find(
        (sistema) => sistema.paneles === panelesEnEsteSistema
      );

      let inversionSistema;
      if (sistemaData) {
        inversionSistema = sistemaData.inversion;
      } else {
        // Si no existe exactamente esa cantidad, usar el sistema más grande disponible
        const sistemaMaximo = paneles_solares?.[0]; // El primero es el de 24 paneles (assuming sorted or default)
        // Fallback safety
        inversionSistema = sistemaMaximo ? sistemaMaximo.inversion : 0;
      }

      inversionGeneral += inversionSistema;
      desgloseSistemas.push({
        paneles: panelesEnEsteSistema,
        inversion: inversionSistema,
      });

      panelesRestantes -= panelesEnEsteSistema;
    }

    return {
      inversionGeneral,
      desgloseSistemas,
      sistemasNecesarios: desgloseSistemas.length,
    };
  };

  const { inversionGeneral, desgloseSistemas, sistemasNecesarios } =
    calcularInversion(numeroPaneles);

  const descuentoContado = 1 - DESCUENTO_CONTADO / 100;
  const inversionTotal = inversionGeneral * descuentoContado;

  // Calcular producción de energía
  const energiaProducidaMensual = numeroPaneles * KWH_MES_PANEL;
  const energiaRequeridaMensual = consumoOriginal;
  // Calcular ahorros base
  const monthlySavings = energiaRequeridaMensual * COSTO_ENERGIA_PROM_SINPANEL;

  // Aplicar multiplicador regional si se proporciona
  let regionMultiplier = 1;
  if (regionId) {
    const porcentajeTemp = guatemalaRegions?.find((d) => d.id === regionId);
    regionMultiplier = porcentajeTemp?.porcentaje
      ? roundTo(porcentajeTemp.porcentaje / 100)
      : 1;
  }

  // Ajustar ahorros por región
  const monthlySavingsAdjusted = roundTo(monthlySavings * regionMultiplier);
  const yearlySavingsAdjusted = monthlySavingsAdjusted * 12;

  // Calcular CO2
  const co2Reduction = roundTo(yearlySavingsAdjusted * FACT_CO2_PROMEDIO_ANUAL);

  // Calcular período de recuperación
  const paybackPeriod = roundTo(inversionTotal / yearlySavingsAdjusted);

  // Calcular nueva factura si se proporciona consumo original
  let monthlyBillAfter = null;
  if (consumoOriginal !== null) {
    const factAnt =
      consumoOriginal *
      COSTO_ENERGIA_PROM_SINPANEL *
      (PORCENTAJE_PAGO_FIJO / 100);
    const restFact =
      energiaRequeridaMensual > consumoOriginal
        ? (energiaRequeridaMensual - consumoOriginal) *
        COSTO_ENERGIA_PROM_CONPANEL
        : (consumoOriginal - energiaRequeridaMensual) *
        COSTO_ENERGIA_PROM_SINPANEL;

    monthlyBillAfter = factAnt + restFact;
  }

  return {
    // Información del sistema
    systemSize: numeroPaneles,
    energiaProducida: energiaProducidaMensual,
    energiaRequerida: energiaRequeridaMensual,
    inversionTotal: inversionTotal,

    // Información financiera
    monthlySavings: monthlySavingsAdjusted,
    yearlySavings: yearlySavingsAdjusted,
    paybackPeriod: paybackPeriod,
    monthlyBillAfter: monthlyBillAfter,

    // Información ambiental
    co2Reduction: co2Reduction,

    // Información técnica
    sistemasNecesarios: sistemasNecesarios,
    desgloseSistemas: desgloseSistemas,

    // Información adicional
    regionMultiplier: regionMultiplier,
    porcentajeCubierto: consumoOriginal
      ? roundTo((energiaRequeridaMensual / consumoOriginal) * 100)
      : null,
  };
};
